import React from 'react';
import { connect } from 'react-redux';
import { ApplicationState } from 'store/rootReducer';
import {
  downloadMockServiceLogsThunk,
  refreshMockServiceLogsThunk,
  setOpenWindowId,
} from 'store/mockServices/mockServiceLogs/mockServiceLogsActions';
import _ from 'lodash';
import { MatchingTxLog, MockService } from 'types/rest/mockServiceTypes';
import styles from 'views/MockServices/Tabs/MockServices/MockServiceLogs.module.css';
import SimpleLoader from 'components/loaders/SimpleLoader';
import { cancelPolling } from 'axios/axios-polling';
import { withTranslation, WithTranslation } from 'react-i18next';
import BzFormControl from 'components/forms/BzFormControl';
import { Col, InputGroup, Modal, Row } from 'react-bootstrap';
import { MAX_LOG_LIMIT } from 'resources/validationConstants';
import { isBase64 } from 'utils/miscUtil';
import BzmIcon from 'components/icons/BzmIcon';
import clsx from 'clsx';
import TagTextFilter from '../../../../components/ui/filters/TagTextFilter';
import { methodColors } from '../../../../utils/commonUtil';
import Placeholder from '../../../../components/placeholder/Placeholder';
import RunnersButtonGroup from '../../../../components/ui/ButtonGroups/RunnersButtonGroup';
import {
  calculateTimes,
  displayJsonDangerous,
  getArrows,
  getStringRepresentation,
  highlightText,
} from './MockServiceLogsUtils';
import { isFeatureEnabled } from '../../../../utils/bzmUserConfigUtil';

type MockServiceLogsProps = StateProps & DispatchProps & LocalProps & WithTranslation;

const convertString = (value: string) => {
  if (value && isBase64(value)) {
    return value ? Buffer.from(value, 'base64').toString() : '';
  } else {
    return value;
  }
};

export interface MatchRow {
  timeMs: number;
  matchDesc: string;
}

const placeholder = <Placeholder icon={'info'} title={'This Log is Empty'} iconSize={70} titleSize={22} />;

class MockServiceLogs extends React.Component<MockServiceLogsProps> {
  state = {
    numberOfLines: 10,
    uniqueWindowId: 0,
    isInvalidLineNumber: false,
    itemsOpen: [],
    filteredLogs: [],
    searchTerm: '',
    activeRunnerTab: 'httpRunner' as any,
  };

  /**
   * Get the service logs based on the activeRunnerTab.
   */
  getServiceLogs = () =>
    this.state.activeRunnerTab === 'messagingRunner'
      ? this.props.serviceLogs.result.messagingRunnerLog
      : this.props.serviceLogs.result.httpRunnerLog;

  logModalClose = (): any => {
    cancelPolling();
    this.props.closeModalHandler();
  };

  componentDidMount = () => {
    const uniqueWindowId = new Date().getTime();
    this.setState({ uniqueWindowId: uniqueWindowId });
    this.props.setOpenWindowId(uniqueWindowId);

    //Virtual Service id, in case of draft, we read it from serviceMockId field
    const id = this.props.mockService.isDraft ? this.props.mockService.serviceMockId : this.props.mockService.id;
    this.props.getMockServiceLogs(id, this.state.numberOfLines, uniqueWindowId, true, this.props.mockService.name);
  };

  setFilteredLogsList = _.debounce((searchTerm: string) => {
    if (searchTerm === '') {
      this.setState({ searchTerm: '', filteredLogs: [] });
      return;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    const servLogs = this.getServiceLogs();
    const fLogs = servLogs.filter(log => {
      const logValues = [
        log.requestTimeStamp,
        String(log.matchFound),
        log.matchedTxnId,
        String(log.liveResponse),
        log.matchType,
        String(log.priority),
        log.request.url,
        log.request.method,
        convertString(log.request.body),
        getStringRepresentation(log.request.headers),
        getStringRepresentation(log.request.cookies),
        String(log.response.status),
        convertString(log.response.body),
        JSON.stringify(log.matchingLog, null, 2),
        getStringRepresentation(log.response.headers),
      ];

      return logValues.some(value => value && value.toLowerCase().includes(lowerSearchTerm));
    });

    this.setState({ searchTerm, filteredLogs: fLogs });
  }, 100);

  /**
   * TODO - refactor this function.
   * @param add
   * @param remove
   */
  getLogs = (add: (item: any) => void, remove: (item: any) => void) => {
    const expandCollapse = (item: any) => {
      this.state.itemsOpen.find(i => i === item) ? remove(item) : add(item);
    };
    const getItemState = (item: string) => {
      return !!this.state.itemsOpen.find(i => i === item);
    };

    const { t } = this.props;
    if (!this.props.serviceLogs) {
      return placeholder;
    } else {
      const serviceLogs = this.getServiceLogs();
      const logSize = _.size(serviceLogs);
      if (logSize === 0) {
        return placeholder;
      }

      const sTerm = this.state.searchTerm.trim();
      const logList = sTerm === '' ? serviceLogs : this.state.filteredLogs;

      return _.map(logList, function(value) {
        const currentIndex = value.txnId + 1;
        const logRequest = 'request' + currentIndex;
        const logResponse = 'response' + currentIndex;
        const logMatchDetails = 'matching' + currentIndex;
        const marginTop = 'mt-2 ml-4';

        return (
          <pre
            key={Math.random()}
            className={`pb-4 bg-light border border-gray infinite-scroll-component ${styles.textWrap}`}
          >
            <h4 className={'font-weight-semi-bold ml-4 mt-3'}>
              {t('mockServiceLogs.request')} {currentIndex}
            </h4>
            <hr className={'mt-0'} />

            {value.requestTimeStamp && (
              <div className={marginTop}>
                <b> {t('mockServiceLogs.timestamp')}: </b>
                <span dangerouslySetInnerHTML={{ __html: ' ' + highlightText(value.requestTimeStamp, sTerm) }} />
              </div>
            )}

            {value.request?.method && (
              <div className={marginTop}>
                <b> {t('mockServiceLogs.method')}: </b>
                <span
                  style={{ color: methodColors[_.get(value, 'request.method', 'ANY')] }}
                  dangerouslySetInnerHTML={{
                    __html: ' ' + highlightText(_.get(value, 'request.method', 'ANY'), sTerm),
                  }}
                />
              </div>
            )}

            {value.request?.url && (
              <div className={marginTop}>
                <b> {t('mockServiceLogs.url')}: </b>
                <span dangerouslySetInnerHTML={{ __html: ' ' + highlightText(_.get(value, 'request.url'), sTerm) }} />
              </div>
            )}

            <div className={marginTop}>
              <b> {t('mockServiceLogs.matched')}: </b>
              <span dangerouslySetInnerHTML={{ __html: ' ' + highlightText(String(value.matchFound), sTerm) }} />
            </div>

            {/* Request */}
            {value.request && (
              <>
                <div className={'ml-3'} style={{ height: '22px' }}>
                  {getArrows(
                    logRequest,
                    t('mockServiceLogs.request'),
                    false,
                    expandCollapse,
                    getItemState,
                    sTerm,
                    styles,
                  )}
                </div>
                {getItemState(logRequest) && (
                  <div className={'ml-4'}>
                    {value.matchFound && (
                      <div className={marginTop}>
                        {t('mockServiceLogs.matchedTransactionId')}:
                        <span dangerouslySetInnerHTML={{ __html: ' ' + highlightText(value.matchedTxnId, sTerm) }} />
                      </div>
                    )}
                    <div className={marginTop}>
                      {t('mockServiceLogs.redirectToLiveSystem')}:
                      <span
                        dangerouslySetInnerHTML={{ __html: ' ' + highlightText(String(value.liveResponse), sTerm) }}
                      />
                    </div>
                    <div className={marginTop}>
                      {t('mockServiceLogs.matchType')}:
                      <span dangerouslySetInnerHTML={{ __html: ' ' + highlightText(value.matchType, sTerm) }} />
                    </div>
                    {value.priority && (
                      <div className={marginTop}>
                        {t('mockServiceLogs.priority')}:
                        <span dangerouslySetInnerHTML={{ __html: ' ' + highlightText(value.priority, sTerm) }} />
                      </div>
                    )}
                    <div className={clsx(marginTop, 'mr-4')}>
                      {' '}
                      {t('mockServiceLogs.headers')}:
                      <span dangerouslySetInnerHTML={{ __html: displayJsonDangerous(value.request.headers, sTerm) }} />
                    </div>
                    <div className={marginTop}>
                      {' '}
                      {t('mockServiceLogs.queryParams')}:
                      <span
                        dangerouslySetInnerHTML={{ __html: displayJsonDangerous(value.request.queryParams, sTerm) }}
                      />
                    </div>
                    <div className={marginTop}>
                      {' '}
                      {t('mockServiceLogs.cookies')}:
                      <span dangerouslySetInnerHTML={{ __html: displayJsonDangerous(value.request.cookies, sTerm) }} />
                    </div>
                    <div className={clsx(marginTop, 'mr-4')}>
                      {' '}
                      {t('mockServiceLogs.body')}:
                      <span
                        dangerouslySetInnerHTML={{
                          __html: ' ' + highlightText(convertString(value.request.body), sTerm),
                        }}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
            {/* Response */}
            {value.response && (
              <>
                <div className={'ml-3'} style={{ height: '22px' }}>
                  {getArrows(
                    logResponse,
                    t('mockServiceLogs.response'),
                    false,
                    expandCollapse,
                    getItemState,
                    sTerm,
                    styles,
                  )}
                </div>
                {getItemState(logResponse) && (
                  <div className={'ml-4'}>
                    <div className={marginTop}>
                      {' '}
                      {t('mockServiceLogs.status')}:
                      <span dangerouslySetInnerHTML={{ __html: ' ' + highlightText(value.response.status, sTerm) }} />
                    </div>
                    <div className={clsx(marginTop, 'mr-4')}>
                      {' '}
                      {t('mockServiceLogs.headers')}:
                      <span dangerouslySetInnerHTML={{ __html: displayJsonDangerous(value.response.headers, sTerm) }} />
                    </div>
                    <div className={clsx(marginTop, 'mr-4')}>
                      {' '}
                      {t('mockServiceLogs.body')}:
                      <span
                        dangerouslySetInnerHTML={{
                          __html: ' ' + highlightText(convertString(value.response.body), sTerm),
                        }}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
            {/* Matching Log */}
            {value.matchingLog && _.size(value.matchingLog.log) !== 0 && (
              <>
                <div className={'ml-3'} style={{ height: '22px' }}>
                  {getArrows(
                    logMatchDetails,
                    t('mockServiceLogs.matchingDetails'),
                    false,
                    expandCollapse,
                    getItemState,
                    sTerm,
                    styles,
                  )}
                </div>
                {getItemState(logMatchDetails) && (
                  <div className={'ml-3 mt-0'}>
                    {value.matchingLog.log !== null &&
                      value.matchingLog.log.map((logItem: MatchingTxLog, index: number) => {
                        const matchTimes = calculateTimes(logItem);
                        const matchDescription = matchTimes.map((logItem: MatchRow, index: number) => (
                          <div className={'mt-2 ml-3'} key={index}>
                            {' '}
                            {logItem.timeMs}
                            {t('mockServiceLogs.matchDescription')}:
                            <span dangerouslySetInnerHTML={{ __html: ' ' + highlightText(logItem.matchDesc, sTerm) }} />
                          </div>
                        ));

                        return (
                          <div className={marginTop} key={index}>
                            <div style={{ height: '22px' }}>
                              <b>
                                {getArrows(
                                  logMatchDetails + logItem.name,
                                  logItem.name + ' (id:' + logItem.id + ')',
                                  true,
                                  expandCollapse,
                                  getItemState,
                                  sTerm,
                                  styles,
                                )}
                              </b>
                            </div>
                            <div className={'ml-4'}>
                              {getItemState(logMatchDetails + logItem.name) && <div>{matchDescription}</div>}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </>
            )}
          </pre>
        );
      });
    }
  };

  lineNumberChanged = (e: any) => {
    const lineNumbers = e.target.value;
    this.setState({
      ...this.state,
      numberOfLines: e.target.value,
      isInvalidLineNumber: !this.isNormalInteger(lineNumbers),
    });
  };

  isNormalInteger = (str: string) => {
    str = str.trim();
    if (!str) {
      return false;
    }
    str = str.replace(/^0+/, '') || '0';
    const n = Math.floor(Number(str));
    return String(n) === str && n > 0 && n <= MAX_LOG_LIMIT;
  };

  // 1. Add a ref to your scrollable div
  logsDivRef = React.createRef<HTMLDivElement>();

  render() {
    const { t } = this.props;
    const loadingIndicator = (
      <div className={`text-center p-5 ${styles.loading}`}>
        <SimpleLoader />
      </div>
    );

    // 2. Update expand/collapse handlers to preserve scroll
    const expandField = item => {
      const scrollTop = this.logsDivRef.current?.scrollTop ?? 0;
      const newSet = new Set(this.state.itemsOpen);
      newSet.add(item);
      this.setState({ itemsOpen: Array.from(newSet) }, () => {
        if (this.logsDivRef.current) {
          this.logsDivRef.current.scrollTop = scrollTop;
        }
      });
    };

    const collapseField = item => {
      const scrollTop = this.logsDivRef.current?.scrollTop ?? 0;
      const newSet = new Set(this.state.itemsOpen);
      newSet.delete(item);
      this.setState({ itemsOpen: Array.from(newSet) }, () => {
        if (this.logsDivRef.current) {
          this.logsDivRef.current.scrollTop = scrollTop;
        }
      });
    };

    return (
      <Modal backdrop={true} show={true} size={'xl'}>
        <Modal.Header>
          <div className="d-inline-flex w-100 justify-content-between">
            <div id={`mockServiceLogsTitle` + this.props.mockService.id} className="font-size-xlg font-weight-bold">
              {t('mockServiceLogs.titlePrefix')}
              &apos;
              {this.props.mockService.name}
              &apos;
              {t('mockServiceLogs.titleSuffix')}:
            </div>

            <button
              type="button"
              id={`mockServiceLogsCloseButton` + this.props.mockService.id}
              title={t('common:actions.close')}
              className="close pt-0"
              aria-label={t('common:actions.close')}
              onClick={() => this.logModalClose()}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
        </Modal.Header>

        <Modal.Body>
          <Row className={'mb-4'}>
            <Col md={4}>
              <TagTextFilter
                className={'ml-auto'}
                searchTerm={''}
                setSearchTerm={searchTerm => {
                  this.setFilteredLogsList(searchTerm);
                }}
                context={'mockServices'}
                placeholder={'Search Log...'}
              />
            </Col>
            <Col md={8} className={'text-right'}>
              <RunnersButtonGroup
                activeRunner={this.state.activeRunnerTab}
                onRunnerChanged={activeRunner => {
                  //Set active tab and clear existing state with expanded details, etc.
                  this.setState({ activeRunnerTab: activeRunner, searchTerm: '', itemsOpen: [], filteredLogs: [] });
                }}
              />
            </Col>
          </Row>
          {/*// 3. Attach the ref to your scrollable div*/}
          <div
            ref={this.logsDivRef}
            id={`mockServiceLogsContent` + this.props.mockService.id}
            className={`overflow-auto ${styles.logsDiv}`}
          >
            {this.props.serviceLogs.isLoading && loadingIndicator}
            {!this.props.serviceLogs.isLoading && this.getLogs(expandField, collapseField)}
          </div>
        </Modal.Body>
        <Modal.Footer className="d-block pt-4 pb-2">
          <Row>
            <Col lg={8}>
              <label htmlFor={`mockServiceLogsNumberOfRequestsInput` + this.props.mockService.id}>
                {t('mockServiceLogs.numberOfRequests')}
              </label>
              <div className="d-inline-flex w-100 justify-content-between">
                <InputGroup className="d-inline-flex w-20">
                  <BzFormControl
                    id={`mockServiceLogsNumberOfRequestsInput` + this.props.mockService.id}
                    required={true}
                    value={this.state.numberOfLines + ''}
                    onChange={e => this.lineNumberChanged(e)}
                    invalid={this.state.isInvalidLineNumber}
                    dirty={true}
                    hideErrors={true}
                  />
                  <InputGroup.Append>
                    <button
                      title={t('mockServiceLogs.refreshMockServiceLogs')}
                      className="btn form-control btn-secondary p-1"
                      onClick={() => {
                        this.setState({ itemsOpen: [] });
                        this.props.getMockServiceLogs(
                          this.props.mockService.id,
                          this.state.numberOfLines,
                          this.state.uniqueWindowId,
                          true,
                          this.props.mockService.name,
                        );
                      }}
                      disabled={this.props.serviceLogs.isLoading}
                    >
                      <BzmIcon variant={'icon-bzm-sync'} className={'icon-xs px-2'} />
                    </button>
                  </InputGroup.Append>
                </InputGroup>
              </div>
              <div className="mb-4">
                {this.state.isInvalidLineNumber && (
                  <div className="invalid-feedback">
                    {t('mockServiceLogs.invalidLineNumber', { maxLogLimit: MAX_LOG_LIMIT })}
                  </div>
                )}
              </div>
            </Col>
            <Col lg={4} className="d-flex align-content-center justify-content-end flex-wrap">
              <button
                id={`mockServiceLogsCancelButton` + this.props.mockService.id}
                className="btn btn-link"
                title={t('common:actions.cancel')}
                onClick={this.logModalClose}
              >
                {t('common:actions.cancel')}
              </button>
              <button
                id={`mockServiceLogsEnabledDownloadButton` + this.props.mockService.id}
                title={t('mockServiceLogs.downloadMockServiceLogs')}
                className="btn btn-primary"
                onClick={() => this.props.downloadMockServiceLogs(this.props.mockService)}
                disabled={this.props.serviceLogs.isLoading || _.size(this.props.serviceLogs.result) === 0}
              >
                {t('common:actions.download')}
              </button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>
    );
  }
}

const mapStateToProps = (state: ApplicationState) => ({
  serviceLogs: state.mockservices.serviceLogs,
});

const mapDispatchToProps = {
  getMockServiceLogs: refreshMockServiceLogsThunk,
  setOpenWindowId,
  downloadMockServiceLogs: downloadMockServiceLogsThunk,
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;
interface LocalProps {
  mockService: MockService;
  closeModalHandler: () => void;
}

export default withTranslation('mockServices')(connect(mapStateToProps, mapDispatchToProps)(MockServiceLogs));
